import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FormCard } from "../../FormCard";
import { DetailRow, InputRow } from "../../Rows";
import Switch from "../../Switch";
import Tooltip from "../../Tooltip";

const ExtrasStep = ({ extras, onChange, currency = "" }) => {
    const { t } = useTranslation();
    const setFee = (key, patch) => {
        onChange({
            ...extras,
            [key]: { ...extras[key], ...patch },
        });
    };

    return (
        <div className="flex flex-col gap-6">
            <FormCard title={t("form.optionalFees")} description={t("tariff.optionalFeesDescription")}>
                <div className="flex flex-col gap-4">
                    <FeeToggle
                        label={t("tariff.chargingTime")}
                        description={t("tariff.chargingTimeDescription")}
                        tooltip={t("tariff.tooltip.chargingTime")}
                        fee={extras.chargingTime}
                        type="TIME"
                        currency={currency}
                        onToggle={(enabled) => setFee("chargingTime", { enabled })}
                        onChange={(patch) => setFee("chargingTime", patch)}
                        showStepSize
                    />
                    <FeeToggle
                        label={t("tariff.parkingTime")}
                        description={t("tariff.parkingTimeDescription")}
                        tooltip={t("tariff.tooltip.parkingTime")}
                        fee={extras.parkingTime}
                        type="PARKING_TIME"
                        currency={currency}
                        onToggle={(enabled) => setFee("parkingTime", { enabled })}
                        onChange={(patch) => setFee("parkingTime", patch)}
                        showStepSize
                    />
                    <FeeToggle
                        label={t("tariff.startFee")}
                        description={t("tariff.startFeeDescription")}
                        tooltip={t("tariff.tooltip.startFee")}
                        fee={extras.startFee}
                        type="FLAT"
                        currency={currency}
                        onToggle={(enabled) => setFee("startFee", { enabled })}
                        onChange={(patch) => setFee("startFee", patch)}
                    />
                    <FeeToggle
                        label={t("tariff.bookingHold")}
                        description={t("tariff.bookingHoldDescription")}
                        tooltip={t("tariff.tooltip.bookingHold")}
                        fee={extras.reservation}
                        type="FLAT"
                        currency={currency}
                        extraTimeFee={toExtraTimeFee(extras.reservation)}
                        extraTimeLabel={t("tariff.whileHeld")}
                        onToggle={(enabled) => setFee("reservation", { enabled })}
                        onChange={(patch) => setFee("reservation", patch)}
                    >
                        <OptionalTimeFields
                            enabled={extras.reservation.enabled}
                            fee={extras.reservation}
                            currency={currency}
                            extraTimeLabel={t("tariff.whileHeld")}
                            stepLabel={t("tariff.holdTimeStep")}
                            onChange={(patch) => setFee("reservation", patch)}
                        />
                    </FeeToggle>
                    {(extras.booking?.enabled || (extras.additionalBookingElements || []).length > 0) && (
                    <FeeToggle
                        label={t("tariff.bookingWindow")}
                        description={t("tariff.bookingWindowDescription")}
                        tooltip={t("tariff.tooltip.bookingWindow")}
                        fee={extras.booking || { enabled: false }}
                        type="FLAT"
                        currency={currency}
                        extraTimeFee={toExtraTimeFee(extras.booking)}
                        extraTimeLabel={t("tariff.overBookedStartEnd")}
                        onToggle={(enabled) => setFee("booking", { enabled })}
                        onChange={(patch) => setFee("booking", patch)}
                    >
                        <OptionalTimeFields
                            enabled={Boolean(extras.booking?.enabled)}
                            fee={extras.booking || {}}
                            currency={currency}
                            extraTimeLabel={t("tariff.overBookedStartEnd")}
                            stepLabel={t("tariff.bookingWindowStep")}
                            onChange={(patch) => setFee("booking", patch)}
                        />
                    </FeeToggle>
                    )}
                    <FeeToggle
                        label={t("tariff.bookingNoShow")}
                        description={t("tariff.bookingNoShowDescription")}
                        tooltip={t("tariff.tooltip.bookingNoShow")}
                        fee={extras.noShow}
                        type="FLAT"
                        currency={currency}
                        onToggle={(enabled) => setFee("noShow", { enabled })}
                        onChange={(patch) => setFee("noShow", patch)}
                    />
                    <FeeToggle
                        label={t("tariff.bookingOvertime")}
                        description={t("tariff.bookingOvertimeDescription")}
                        tooltip={t("tariff.tooltip.bookingOvertime")}
                        fee={extras.overtime || { enabled: false }}
                        type="FLAT"
                        currency={currency}
                        extraTimeFee={toExtraTimeFee(extras.overtime)}
                        extraTimeLabel={t("tariff.afterBookedEnd")}
                        onToggle={(enabled) => setFee("overtime", { enabled })}
                        onChange={(patch) => setFee("overtime", patch)}
                    >
                        <OptionalTimeFields
                            enabled={Boolean(extras.overtime?.enabled)}
                            fee={extras.overtime || {}}
                            currency={currency}
                            stepLabel={t("tariff.overtimeStep")}
                            onChange={(patch) => setFee("overtime", patch)}
                        />
                    </FeeToggle>
                    <FeeToggle
                        label={t("tariff.bookingCancellation")}
                        description={t("tariff.bookingCancellationDescription")}
                        tooltip={t("tariff.tooltip.bookingCancellation")}
                        fee={extras.cancellation || { enabled: false }}
                        type="FLAT"
                        currency={currency}
                        extraTimeFee={toExtraTimeFee(extras.cancellation)}
                        extraTimeLabel={t("tariff.bookedDuration")}
                        onToggle={(enabled) => setFee("cancellation", { enabled })}
                        onChange={(patch) => setFee("cancellation", patch)}
                    >
                        <OptionalTimeFields
                            enabled={Boolean(extras.cancellation?.enabled)}
                            fee={extras.cancellation || {}}
                            currency={currency}
                            stepLabel={t("tariff.cancellationStep")}
                            onChange={(patch) => setFee("cancellation", patch)}
                        />
                    </FeeToggle>
                </div>
            </FormCard>
        </div>
    );
};

const FeeToggle = ({
    label,
    description,
    tooltip,
    fee,
    type,
    currency,
    extraTimeFee,
    extraTimeLabel,
    onToggle,
    onChange,
    showStepSize = false,
    children,
}) => {
    const { t } = useTranslation();
    const showTimeSummary =
        extraTimeFee && extraTimeFee.price !== "" && extraTimeFee.price != null;
    const timeLabel = extraTimeLabel || t("tariff.whileBooked");

    return (
        <div className="rounded-lg border border-mute-200 px-4 py-3 [&_div.py-4]:border-b-0">
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold">{label}</div>
                        {tooltip && <InfoTip text={tooltip} />}
                    </div>
                    {description ? (
                        <div className="text-sm text-mute-600 mt-0.5">{description}</div>
                    ) : null}
                    {fee.enabled ? (
                        <>
                            <FeeSummary price={fee.price} vat={fee.vat} stepSize={fee.stepSize} type={type} currency={currency} />
                            {showTimeSummary && (
                                <>
                                    <div className="text-sm text-mute-600 mt-2">{timeLabel}</div>
                                    <FeeSummary
                                        price={extraTimeFee.price}
                                        vat={extraTimeFee.vat}
                                        stepSize={extraTimeFee.stepSize}
                                        type="TIME"
                                        currency={currency}
                                    />
                                </>
                            )}
                        </>
                    ) : (
                        <div className="text-sm text-mute-600 mt-0.5">{t("tariff.notIncluded")}</div>
                    )}
                </div>
                <Switch checked={fee.enabled} onChange={onToggle} />
            </div>
            {fee.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 border-t border-mute-200 mt-3">
                    <FeePriceVatFields
                        price={fee.price}
                        vat={fee.vat}
                        currency={currency}
                        unit={type === "TIME" || type === "PARKING_TIME" ? "/ h" : ""}
                        priceLabel={type === "FLAT" ? t("form.fixedPriceExcl") : undefined}
                        inclLabel={type === "FLAT" ? t("form.fixedPriceIncl") : undefined}
                        onPriceChange={(value) => onChange({ price: value })}
                        onVatChange={(value) => onChange({ vat: value })}
                    />
                    {isZeroPriceWithOtherValues(
                        fee.price,
                        fee.vat,
                        showStepSize ? fee.stepSize : undefined
                    ) && <ZeroPriceWarning hasStepSize={showStepSize} />}
                    {showStepSize && (
                        <InputRow
                            label={t("form.stepSizeS")}
                            tooltip={t("tariff.tooltip.stepSize")}
                            value={fee.stepSize}
                            isNumber
                            onChange={(value) => onChange({ stepSize: value })}
                        />
                    )}
                    {showStepSize && isUnusuallyShortStep(fee.stepSize) && (
                        <StepSizeWarning stepSize={fee.stepSize} />
                    )}
                    {children}
                </div>
            )}
        </div>
    );
};

function toExtraTimeFee(fee) {
    return {
        price: fee?.timePrice,
        vat: fee?.timeVat,
        stepSize: fee?.timeStepSize,
    };
}

function OptionalTimeFields({ enabled, fee, currency, extraTimeLabel, stepLabel, onChange }) {
    const { t } = useTranslation();
    if (!enabled) {
        return null;
    }
    return (
        <>
            <div className="md:col-span-2 mt-4 pt-3 border-t border-mute-200">
                <p className="text-sm font-semibold">{t("common.hourlyOptional")}</p>
                {extraTimeLabel ? (
                    <p className="text-sm text-mute-600 mt-1">{extraTimeLabel}</p>
                ) : null}
            </div>
            <FeePriceVatFields
                price={fee.timePrice}
                vat={fee.timeVat}
                currency={currency}
                unit="/ h"
                onPriceChange={(value) => onChange({ timePrice: value })}
                onVatChange={(value) => onChange({ timeVat: value })}
            />
            <InputRow
                label={stepLabel}
                tooltip={t("tariff.tooltip.stepSize")}
                value={fee.timeStepSize}
                isNumber
                onChange={(value) => onChange({ timeStepSize: value })}
            />
            {isUnusuallyShortStep(fee.timeStepSize) && (
                <StepSizeWarning stepSize={fee.timeStepSize} />
            )}
            {isZeroPriceWithOtherValues(fee.timePrice, fee.timeVat, fee.timeStepSize) && (
                <ZeroPriceWarning hasStepSize />
            )}
        </>
    );
}

function FeePriceVatFields({
    price,
    vat,
    currency,
    unit = "",
    priceLabel,
    inclLabel,
    onPriceChange,
    onVatChange,
}) {
    const { t } = useTranslation();
    return (
        <>
            <InputRow
                label={priceLabel || (unit ? t("form.pricePerHourExcl") : t("form.priceExclVat"))}
                tooltip={t("tariff.tooltip.price")}
                value={price}
                isNumber
                onChange={onPriceChange}
            />
            <InputRow
                label={t("form.vatPercent")}
                tooltip={t("tariff.tooltip.vat")}
                value={vat}
                isNumber
                onChange={onVatChange}
            />
            <DetailRow
                label={inclLabel || (unit ? t("form.pricePerHourIncl") : t("form.priceInclVat"))}
                value={formatPriceInclVat(price, vat, currency)}
            />
        </>
    );
}

function formatPriceInclVat(price, vat, currency) {
    const excl = Number(price);
    if (price === "" || price == null || !Number.isFinite(excl)) {
        return "";
    }
    const vatPct = vat === "" || vat == null ? 0 : Number(vat);
    if (!Number.isFinite(vatPct)) {
        return "";
    }
    const incl = excl * (1 + vatPct / 100);
    const amount = formatPriceAmount(incl);
    return currency ? `${amount} ${currency}` : amount;
}

function formatPriceAmount(value) {
    const rounded = Math.round(value * 10000) / 10000;
    if (Number.isInteger(rounded)) {
        return String(rounded);
    }
    return String(rounded);
}

function FeeSummary({ price, vat, stepSize, type, currency }) {
    const { t } = useTranslation();
    const excl = formatFeePrice(price, vat, type, currency, false, t);
    const incl = formatFeePrice(price, vat, type, currency, true, t);
    const meta = feeMeta(price, vat, stepSize, type, currency, t);
    const timed = type === "TIME" || type === "PARKING_TIME";
    const unusual =
        (timed && isUnusuallyShortStep(stepSize)) ||
        isZeroPriceWithOtherValues(price, vat, timed ? stepSize : undefined);
    const priceClass = `text-sm font-semibold ${unusual ? "text-warning-700" : ""}`;
    return (
        <>
            <div className={`${priceClass} mt-2`}>{excl}</div>
            {incl ? <div className={`${priceClass} mt-0.5`}>{incl}</div> : null}
            {meta ? (
                <div className={`text-sm mt-0.5 ${unusual ? "text-warning-700" : "text-mute-600"}`}>{meta}</div>
            ) : null}
        </>
    );
}

function StepSizeWarning({ stepSize }) {
    const { t } = useTranslation();
    const label = formatStepLabel(Number(stepSize), t);
    return (
        <p className="md:col-span-2 text-sm text-warning-700 mt-1">
            {t("tariff.stepSizeWarning", { label })}
        </p>
    );
}

function ZeroPriceWarning({ hasStepSize = false }) {
    const { t } = useTranslation();
    return (
        <p className="md:col-span-2 text-sm text-warning-700 mt-1">
            {hasStepSize ? t("tariff.zeroPriceStep") : t("tariff.zeroPriceVat")}
        </p>
    );
}

function InfoTip({ text }) {
    return (
        <Tooltip content={text} position="right" tooltipClassName="max-w-lg px-2 py-1">
            <span
                className="inline-flex shrink-0 cursor-help text-mute-400 hover:text-mute-600"
                aria-label={text}
            >
                <FontAwesomeIcon icon={faInfoCircle} className="h-3.5 w-3.5" />
            </span>
        </Tooltip>
    );
}

function formatFeePrice(price, vat, type, currency, inclVat, t) {
    const excl = Number(price);
    if (price == null || price === "" || !Number.isFinite(excl)) {
        return inclVat ? "" : "-";
    }
    const vatPct = vat === "" || vat == null ? 0 : Number(vat);
    const amount = inclVat && Number.isFinite(vatPct) ? excl * (1 + vatPct / 100) : excl;
    const unit = type === "TIME" || type === "PARKING_TIME" ? "/h" : "";
    const vatLabel = inclVat ? t("common.inclVat") : t("common.exclVat");
    const formatted = formatPriceAmount(amount);
    const withCurrency = currency ? `${formatted} ${currency}${unit}` : `${formatted}${unit}`;
    return `${withCurrency} ${vatLabel}`;
}

function feeMeta(price, vat, stepSize, type, currency, t) {
    const parts = [];
    const perStep = formatPerStepCharge(price, vat, stepSize, type, currency, t);
    if (perStep) {
        parts.push(perStep);
    }
    if (vat != null && vat !== "") {
        parts.push(t("tariff.vatPercentValue", { vat }));
    }
    return parts.join(" · ");
}

function formatPerStepCharge(price, vat, stepSize, type, currency, t) {
    if (type !== "TIME" && type !== "PARKING_TIME") {
        return "";
    }
    const step = Number(stepSize);
    const amountPerHour = Number(price);
    if (!Number.isFinite(step) || step < 1) {
        return "";
    }
    const unit = formatStepLabel(step, t);
    if (!Number.isFinite(amountPerHour) || price === "" || price == null) {
        return t("tariff.everyStarted", { unit });
    }
    const vatPct = vat === "" || vat == null ? 0 : Number(vat);
    const exclAmount = formatPriceAmount(amountPerHour * (step / 3600));
    const inclAmount = Number.isFinite(vatPct)
        ? formatPriceAmount(amountPerHour * (1 + vatPct / 100) * (step / 3600))
        : null;
    const exclText = currency
        ? t("tariff.chargedEveryStartedExcl", { amount: exclAmount, currency, unit })
        : t("tariff.chargedEveryStartedExclNoCurrency", { amount: exclAmount, unit });
    if (inclAmount == null) {
        return exclText;
    }
    const inclText = currency
        ? t("tariff.everyStartedIncl", { amount: inclAmount, currency, unit })
        : t("tariff.everyStartedInclNoCurrency", { amount: inclAmount, unit });
    return `${exclText} · ${inclText}`;
}

function isUnusuallyShortStep(stepSize) {
    const step = Number(stepSize);
    return Number.isFinite(step) && step >= 1 && step < 60;
}

function isExplicitZero(value) {
    return value !== "" && value != null && Number(value) === 0;
}

function isFilled(value) {
    return value !== "" && value != null;
}

function isZeroPriceWithOtherValues(price, vat, stepSize) {
    if (!isExplicitZero(price)) {
        return false;
    }
    return isFilled(vat) || isFilled(stepSize);
}

function formatStepLabel(step, t) {
    if (step % 60 === 0) {
        return t("tariff.stepMin", { n: step / 60 });
    }
    return t("tariff.stepSec", { n: step });
}

export default ExtrasStep;
